import { Tag } from "./tags.types";
import { WKElement, WKStrategy, WKStrategyStep } from "./types_wk";

export interface WKExerciseSpec {
    audience: string;
    modes?: string[];
    showHints?: boolean;
    translations?: any;
    scriptRef?: string;
    script?: string;
    definitions: { [key: string]: string };
    localVars?: any;
    resourceMap?: { [refId: string]: any }
    interactionMap: { [key: string]: any };
    exerciseContext?: any;
  }
  
export class ExerciseSpec {
    audience: string;
    elements: any[]
}

export type AkitCreateSessionResponse = AkitCreateSessionsForExerciseResult[] | AkitErrorResponse;

export class AkitErrorResponse {
    success: false;
    msg: string;
}
export class AkitCreateSessionsForExerciseResult {
    success: boolean;
    msg?: string;
    sessions: AkitCreateSessionResult[];
}

export interface AkitCreateSessionResult {
    sessionId: string;
    type: string;
    solution: boolean,
    html?: any;
    data?: any;
    success?: boolean
    msg?: string;
    errorData?: any,
    debugInformation?: any;
    marksTotal?: number; //marks for this whole exercise, depends on scoring model
    interactionMarks?: {[key:string]:number}
}

export interface EvaluationItem {
    type: string;
    expression: string;
    syntax: string;
    attributes?: string[]
}

export interface SkillTag {
    id: string;
    weight: number;
    errors?: string[];
    source?: TagSource
}
  
export enum TagSource {
ERROR_FEEDBACK='ERROR_FEEDBACK',
FINISHED_STEP='FINISHED_STEP',
HINT='HINT',
NEXT_STEP='NEXT_STEP'
}
  
  
export interface IErrorData {
    msg: string;
    data: any;
}

// output from akit-exercise-editor
export class AuthoredExercise {
    id: string;
    name: string;
    majorVersion: number;
    type: string = "SPECIFICATION";
    definition: ExerciseSpec;
    metadata: any;
  }
  

export enum AkitLearningEventType {
    EVALUATE = "EVALUATE",
    HINT = "HINT",
}
export interface AkitLearningEvent {
    type: AkitLearningEventType;
    marksEarned: number;
    marksTotal: number;
    finished: boolean;
    tags: SkillTag[];
}

export interface AkitPristineChangedEvent {
    pristine: boolean;
}

export interface ExerciseResult {
    scoring: Scorable,
    questions: QuestionResult[]
    tagDescriptions: {[key: string]: Tag};
}


export interface InteractionData {
    refName: string;
    refId: string;
    type: string;
    block: string;
}

export interface ExerciseData {
    name: string;
    interactions: InteractionData[];
}

export interface Scorable {
    finished: boolean;
    marksEarned: number;
    marksTotal: number;
}
export interface QuestionResult {
    id: string,
    scoring?: Scorable,
    interactions: InteractionResult[]
}

export interface InteractionResult {
    id: string;
    /** interaction type, e.g. MULTISTEP, etc */
    type: string;
    /** standardized evaluation state. E.g. FINISHED, CORRECT, UNKNOWN, etc */
    status: EvaluationStatus,
    /** progress in range [0..1]. Note that progress is not equivalent to scoring (nr of marks) */
    progress: number,
    /** result of the scoring (marking) process */
    scoring: InteractionScore,
    /** tags indicating (lack of) learning skills */
    tags: SkillTag[];
    /** standardized list of learning events (evaluation, hint, give up, etc) */
    events: ExportedEventResult[],
}

/**
 * Every question type has its own specific data scheme for the events it supports. 
 * E.g. The evaluation result of Multistep is very different from the evaluation result
 * of Geometry.
 * ExportedEventResult provides a unified scheme to share information about evaluation, hints, etc
 * to the client. So the exported evaluation result of Geometry and Multistep use this same interface.
 */
 export interface ExportedEventResult {
    event: InteractionEventType;
    timestamp: number;

    /** Note: SkillTag is not the same as WKSkillTag, which is the output of the Akit Engine */
    tags: SkillTag[];

    /** meaningful information that can be shared to a client platform. E.g hints or error feedback */
    annotations: Annotation[],
}

/** An unified interface to share hints, feedback of the question type */
export interface Annotation {
    type: AnnotationType;
    main?: WKElement[];
    sub?: WKElement[];
    expr?: WKElement[];
}

export enum AnnotationType {
    HINT = "HINT",
    ERROR_FEEDBACK = "ERROR_FEEDBACK",
    INPUT_EXPRESSION = "INPUT_EXPRESSION"
}

export interface ExportedEvaluationEventResult extends ExportedEventResult{
    /** standard type for evaluation result of the exercise */
    event: InteractionEventType.EVALUATE;
    exerciseStatus: EvaluationStatus;
    inputStatus?: string;
    // derivationTags?: string[];
    // remainingTags?: string[];
    progress: number;
}
export function isExportedEvaluationEventResult(obj: ExportedEventResult): obj is ExportedEvaluationEventResult {
    return (obj as ExportedEvaluationEventResult).exerciseStatus != null;
}

export interface ExportedHintEventResult extends ExportedEventResult {
    event: InteractionEventType.HINT
}

export function isExportedHintEventResult(obj: ExportedEventResult): obj is ExportedHintEventResult {
    return (obj as ExportedHintEventResult).event == InteractionEventType.HINT;
}

export interface ExportedGiveUpEventResult extends ExportedEventResult {

}

export interface ExportedSubmitEventResult extends ExportedEventResult {
    
}

export enum InteractionEventType {
    EVALUATE='EVALUATE',
    HINT='HINT',
    GIVEUP='GIVEUP',
    SUBMIT='SUBMIT',
}

export enum EvaluationStatus {
    FINISHED = "FINISHED",
    CORRECT = "CORRECT",  // correct but not finished
    ERROR = "ERROR",
    VIRGIN = "VIRGIN",  // no evaluations have happened (but hints might have been given)
    UNKNOWN = "UNKNOWN"
}

export interface InteractionScore extends Scorable {
    penalties: ScorePenalties
    
    //Marks min-max score, depending on steps that were done and which not. max includes steps that are implicitly covered
    marks?: ScoreRange; 
    stepList?: WKStrategyStep[];

    //TO BE REMOVED
    //sequence of steps chosen by the student
    strategy?: WKStrategy;
    //TO BE REMOVED
    //marks per step
    stepMarks?: {[stepName: string]:ScoreRange};

    failed?: boolean;
}
export interface ScorePenalties {
    marksPenalty: number;
    hintsRequested: number;
    mathErrors: number;
    notationErrors: number;
}
export interface ScoreRange {
    min: number;
    max: number;
}

export interface InteractionInfo {
    type: string;
    marks: number
}
export interface ValidateExercisResult {
    valid: boolean;
    msg?: string; //error message if not valid
    random: boolean;
    marks: number;
    interactions: { [refId: string]: InteractionInfo },
    errorData?: any
    debugInfo?: any;
}
